/**
 * Liest einen verschachtelten Wert aus einem Objekt anhand eines
 * punktgetrennten Pfads aus.
 */
export function getByPath(obj, path) {
    return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

/**
 * Prüft, ob ein Wert als leer behandelt werden soll,
 * zum Beispiel null, leerer String, leeres Array oder leeres Objekt.
 * Zusätzlich gelten numerische Nullwerte ("0", 0) als leer, weil
 * Bundle-Zähler aus PHP als String "0" ankommen können.
 */
export function isEmptyValue(v) {
    if (v == null) return true;
    if (typeof v === "number") return v === 0 || Number.isNaN(v);
    if (typeof v === "string") {
        const s = v.trim();
        if (s.length === 0) return true;
        return s === "0";
    }
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === "object") return Object.keys(v).length === 0;
    return false;
}

/**
 * Entfernt ein Element innerhalb eines Roots, wenn ein Selektor angegeben ist
 * und das Element ausgeblendet werden soll. Entfernt alle Treffer, damit auch
 * wiederholte Elemente wie ".weekly__bundle--item" sauber verschwinden.
 */
export function collapse(root, selector, shouldCollapse) {
    if (!selector || !shouldCollapse) return;
    root.querySelectorAll(selector).forEach((el) => el.remove());
}

/**
 * Entfernt Container, die nach der Injektion keine sichtbaren Kinder mehr haben.
 */
export function collapseEmptyContainers(root, selectors = []) {
    for (const sel of selectors) {
        root.querySelectorAll(sel).forEach((el) => {
            if (el.children.length === 0 && (el.textContent || "").trim() === "") el.remove();
        });
    }
}

/**
 * Ermittelt die Ziel-Elemente einer Mapping-Regel.
 * Mit "all: true" werden alle Treffer bespielt, sonst nur der erste.
 */
export function resolveTargets(root, selector, all = false) {
    if (!selector) return [];
    if (all) return [...root.querySelectorAll(selector)];
    const el = root.querySelector(selector);
    return el ? [el] : [];
}

/**
 * Normalisiert eine URL auf ihren Pfad, ohne Query, Hash und Trailing-Slash.
 * Dadurch matcht /portal/de/irankrieg/?search=1 auch auf
 * /portal/de/irankrieg oder eine absolute Variante derselben Seite.
 */
export function normalizeHref(href, base = window.location.href) {
    if (!href) return "";
    try {
        const u = new URL(String(href), base);
        const path = u.pathname.replace(/\/+$/, "");
        return (path || "/").toLowerCase();
    } catch {
        return String(href).trim().replace(/[?#].*$/, "").replace(/\/+$/, "").toLowerCase();
    }
}

/**
 * Stellt sicher, dass der Pfad einer URL mit "/" endet, also auch dann,
 * wenn eine Query oder ein Hash folgt.
 *
 *   /portal/de/irankrieg?search=1  -> /portal/de/irankrieg/?search=1
 *   /portal/de/irankrieg           -> /portal/de/irankrieg/
 *   /portal/de/irankrieg/?search=1 -> unverändert
 *
 * Unangetastet bleiben Datei-URLs (letztes Segment enthält einen Punkt,
 * z. B. /foo/bar.html) sowie Nicht-HTTP-Schemata (mailto:, tel:, #anker).
 * Absolute Links bleiben absolut, relative bleiben relativ.
 */
export function ensureTrailingSlash(href) {
    if (!href) return href;

    const raw = String(href).trim();
    if (!raw) return raw;
    if (/^(mailto:|tel:|javascript:|data:|#)/i.test(raw)) return raw;

    // Pfad von Query/Hash trennen, ohne die Schreibweise zu verändern.
    const m = raw.match(/^([^?#]*)([?#].*)?$/);
    if (!m) return raw;

    const path = m[1];
    const rest = m[2] || "";

    if (!path || path.endsWith("/")) return raw;

    const lastSegment = path.split("/").pop() || "";
    if (lastSegment.includes(".")) return raw;

    return `${path}/${rest}`;
}

/**
 * Leitet die URL der Übersichtsseite aus einer Detail-URL ab.
 * /portal/de/irankrieg/?search=1 -> /portal/de/bundles/
 */
export function buildIndexUrl(href, segment, base = window.location.href) {
    if (!segment) return null;
    try {
        const u = new URL(String(href), base);
        const parts = u.pathname.split("/").filter(Boolean);
        if (parts.length >= 2) return `/${parts[0]}/${parts[1]}/${segment}/`;
        if (parts.length === 1) return `/${parts[0]}/${segment}/`;
    } catch {
        /* Fallback unten */
    }
    return `/${segment}/`;
}

/**
 * Sucht auf einer Übersichtsseite die Kachel, deren Attribut (z. B. data-href)
 * auf dieselbe Seite zeigt wie der übergebene Link.
 */
export function findItemByHref(indexDoc, href, itemAttr = "data-href") {
    if (!indexDoc || !href) return null;

    const wanted = normalizeHref(href);
    if (!wanted) return null;

    const candidates = indexDoc.querySelectorAll(`[${itemAttr}]`);
    for (const el of candidates) {
        if (normalizeHref(el.getAttribute(itemAttr)) === wanted) return el;
    }

    return null;
}

/**
 * Liefert das JSON-Script innerhalb der passenden Kachel einer Übersichtsseite.
 */
export function findItemScriptByHref(indexDoc, href, itemAttr = "data-href", jsonSelector = "script.item-data") {
    const item = findItemByHref(indexDoc, href, itemAttr);
    return item ? item.querySelector(jsonSelector) : null;
}

/**
 * Sucht das JSON-Script in einem geladenen Dokument.
 * Reihenfolge: data-json-id am Anchor, jsonId der Map,
 * jsonSelector der Map, generischer Fallback.
 */
export function resolveDataScript(doc, anchorEl, mapDef) {
    const anchorId = anchorEl?.dataset?.jsonId || anchorEl?.getAttribute?.("data-json-id");

    const byId = [anchorId, mapDef?.jsonId].filter(Boolean);
    for (const id of byId) {
        const el = doc.getElementById(id);
        if (el) return el;
    }

    const bySelector = [
        mapDef?.jsonSelector,
        'script[type="application/json"][data-inject-data]',
    ].filter(Boolean);

    for (const sel of bySelector) {
        try {
            const el = doc.querySelector(sel);
            if (el) return el;
        } catch {
            /* ungültiger Selektor wird ignoriert */
        }
    }

    return null;
}

/**
 * Parst den Inhalt eines JSON-Scripts defensiv.
 */
export function parseJsonScript(scriptEl) {
    if (!scriptEl) return null;
    try {
        const raw = (scriptEl.textContent || "").trim();
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

/**
 * Verhindert, dass Klicks auf data-search-Elemente innerhalb eines Links
 * die eigentliche Linknavigation auslösen.
 */
export function bindSearchDelegation(anchorEl) {
    if (anchorEl.__weeklyDelegationBound) return;
    anchorEl.__weeklyDelegationBound = true;

    anchorEl.addEventListener("click", (e) => {
        const tag = e.target?.closest?.("[data-search]");
        if (!tag) return;
        e.preventDefault();
        e.stopPropagation();
    });
}
